import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useRef, type CSSProperties, type KeyboardEvent } from 'react';
import { BiX } from 'react-icons/bi';
import { MdDragHandle } from 'react-icons/md';

import { CHECKLIST_ITEMS_MAX, CHECKLIST_TEXT_MAX } from '@/store/tasks/tasks.dto';

import AddButton from '../Button/AddButton';

import { newDraft, type ChecklistDraft } from './checklistDrafts';
import s from './TaskChecklist.module.scss';

// Grows with what is typed, so a long step is read whole rather than cut off.
const fit = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

const EditorRow = ({
  item,
  onText,
  onKeyDown,
  onRemove,
  register,
}: {
  item: ChecklistDraft;
  onText: (text: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onRemove: () => void;
  register: (el: HTMLTextAreaElement | null) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.draftId });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 1 : undefined,
  };
  const name = item.text.trim() || 'Step';

  return (
    <li ref={setNodeRef} style={style} className={s.editorRow}>
      <button
        type="button"
        ref={setActivatorNodeRef}
        className={s.grip}
        aria-label={`Reorder ${name}`}
        {...attributes}
        {...listeners}
      >
        <MdDragHandle size={20} aria-hidden="true" />
      </button>
      <textarea
        ref={(el) => {
          register(el);
          if (el) fit(el);
        }}
        className={s.editorInput}
        rows={1}
        value={item.text}
        placeholder="Close the seacock"
        maxLength={CHECKLIST_TEXT_MAX}
        enterKeyHint="next"
        aria-label="Step"
        onChange={(e) => {
          fit(e.currentTarget);
          // A step is one line: Enter starts the next one, and a pasted break is a space.
          onText(e.currentTarget.value.replace(/\s*\n+\s*/g, ' '));
        }}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className={s.remove}
        // As TaskFiles in the same sheet: a saved step is deleted, an unsaved one only removed.
        aria-label={`${item.id ? 'Delete' : 'Remove'} ${name}`}
        onClick={onRemove}
      >
        <BiX size={16} aria-hidden="true" />
      </button>
    </li>
  );
};

/**
 * The checklist as the task sheet writes it: one row per step, in the order they will
 * be done. Enter opens the next row, Backspace on an empty row removes it, and the grip
 * reorders. Ticking belongs to the task itself (`TaskChecklist`), so the sheet keeps
 * each step's status as it found it.
 *
 * @alias checklist editor, steps, subtasks, sortable list
 */
export const ChecklistEditor = ({
  value,
  onChange,
}: {
  value: ChecklistDraft[];
  onChange: (next: ChecklistDraft[]) => void;
}) => {
  const inputs = useRef(new Map<string, HTMLTextAreaElement>());
  // The row to focus once the change that created or revealed it has rendered.
  const pendingFocus = useRef<string | null>(null);

  // No dependency list on purpose: it runs after every render and acts only when a change
  // has left a row to focus.
  useEffect(() => {
    const id = pendingFocus.current;
    if (!id) return;
    pendingFocus.current = null;
    inputs.current.get(id)?.focus();
  });

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const insertAfter = (index: number) => {
    if (value.length >= CHECKLIST_ITEMS_MAX) return;
    const draft = newDraft();
    pendingFocus.current = draft.draftId;
    onChange([...value.slice(0, index + 1), draft, ...value.slice(index + 1)]);
  };

  const handleKeyDown = (index: number) => (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      insertAfter(index);
    } else if (e.key === 'Backspace' && value[index].text === '') {
      e.preventDefault();
      pendingFocus.current = (value[index - 1] ?? value[index + 1])?.draftId ?? null;
      onChange(value.filter((_, i) => i !== index));
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = value.findIndex((d) => d.draftId === active.id);
    const to = value.findIndex((d) => d.draftId === over.id);
    onChange(arrayMove(value, from, to));
  };

  return (
    <section className={s.editor}>
      <p className={s.editorTitle}>Checklist{value.length > 0 && ` (${value.length})`}</p>
      {value.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={value.map((d) => d.draftId)}
            strategy={verticalListSortingStrategy}
          >
            <ul className={s.editorList}>
              {value.map((item, i) => (
                <EditorRow
                  key={item.draftId}
                  item={item}
                  onText={(text) =>
                    onChange(value.map((d) => (d.draftId === item.draftId ? { ...d, text } : d)))
                  }
                  onKeyDown={handleKeyDown(i)}
                  onRemove={() => onChange(value.filter((d) => d.draftId !== item.draftId))}
                  register={(el) => {
                    if (el) inputs.current.set(item.draftId, el);
                    else inputs.current.delete(item.draftId);
                  }}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
      {value.length < CHECKLIST_ITEMS_MAX && (
        <AddButton label="Step" onClick={() => insertAfter(value.length - 1)} />
      )}
    </section>
  );
};
